<svelte:options accessors={true} immutable={true} />

<script>
  import { onMount, createEventDispatcher } from 'svelte';
  import { initNavState, initLoggedIn, initCartCount, initCurrentUrl, setFlags } from '../../js/stores';
  import { getUrl } from '../../js/urlUtils';
  import Header from './Header/Header.svelte';
  import SquareSans from './SquareSans/SquareSans.svelte';
  import { DESKTOP_BREAKPOINT } from '../../js/constants';

  export let dataJSON = undefined;
  export let imageSubnavJSON = undefined;
  export let ctaSubnavJSON = undefined;
  export let flagsJSON = undefined;
  export let loggedIn = undefined;

  export let darkMode = false;
  export let themeColor = '#006aff';
  export let themeFocusColor = '#0055cc';
  export let zIndex = 1000;
  export let cartCount = 0;
  export let currentUrl = getUrl();

  const navStateStore = initNavState();
  const loggedInStore = initLoggedIn(loggedIn);
  const cartCountStore = initCartCount(cartCount);
  const currentUrlStore = initCurrentUrl(currentUrl);
  const dispatch = createEventDispatcher();

  // width bound to window's innerWidth below
  let innerWidth;

  // width bound to nav width below
  let clientWidth;

  $: desktopMode = innerWidth === undefined ? null : innerWidth >= DESKTOP_BREAKPOINT;
  $: mobileMode = desktopMode === false;

  // When props change, update the stores
  $: $loggedInStore = loggedIn;
  $: $cartCountStore = cartCount;
  $: $currentUrlStore = currentUrl;

  // When desktopMode changes, reset the navState
  $: $navStateStore = desktopMode ? {} : {};

  $: data = dataJSON ? JSON.parse(dataJSON) : {};

  $: imageSubnavData = imageSubnavJSON ? JSON.parse(imageSubnavJSON) : undefined;

  $: ctaSubnavData = ctaSubnavJSON ? JSON.parse(ctaSubnavJSON) : undefined;

  $: if (data.cart && data.cart.dataUrl) {
    if (typeof window !== 'undefined') {
      fetchCartData();
    }
  }

  $: if (loggedIn === undefined) {
    if (typeof window !== 'undefined') {
      fetchLoggedInState();
    }
  }

  // fetchCartData() returns the fetch promise
  // so it can be chained from outside the component
  export function fetchCartData() {
    return fetch(data.cart.dataUrl)
      .then((response) => response.json())
      .then((data) => {
        cartCount = data.item_count;
      })
      .catch((error) => {
        console.error('PublicWebNav: Unable to fetch cart data', error);
      });
  }

  // fetchLoggedInState() returns the fetch promise
  // so it can be chained from outside the component
  export function fetchLoggedInState() {
    return fetch('/mp/status')
      .then((response) => {
        if (response.status === 200) {
          loggedIn = true;
        }
        // default loggedIn is false so we don't need an else case here
        return response.json();
      })
      .catch((error) => {
        console.error('PublicWebNav: Unable to fetch logged in state', error);
      });
  }
  let bodyHasMaxWidth = false; // bodyHasMaxWidth is used to determine if the nav outer gutter should be applied by setting --dynamic-gutter

  onMount(() => {
    // Initialize feature flag store
    setFlags(flagsJSON);

    dispatch('mount', { meta: data && data.meta });
    // If the page is rendered using xms, xms2, or solidshop and it's in the old grid, the body element will have a max-width set
    // If the page is rendered using shop FE, the body element would not have a max-width property but it is set to a shop layout container. This container have a class name that starts with "shop-layout"
    // nav outer gutter is only needed for the page that does not have a max-width set
    const bodyMaxWidth = getComputedStyle(document.body).getPropertyValue('max-width'); // find out if the page has a max-width set
    const isShopFe = document.querySelector('[class^="shop-layout"]'); // find out if the page is rendered using shop FE
    bodyHasMaxWidth = (bodyMaxWidth && bodyMaxWidth !== 'none') || Boolean(isShopFe); // if the page has a max-width set or is rendered using shop FE, bodyHasMaxWidth would be true
    // set --dynamic-gutter below if the page does not have a max width set
  });
</script>

<SquareSans />

<svelte:window bind:innerWidth />

<div
  class="PublicWebNav"
  class:darkMode
  data-nav-monorepo
  data-nav-device-type={desktopMode ? 'Desktop' : 'Mobile'}
  bind:clientWidth
  style:--dynamic-gutter={bodyHasMaxWidth ? '0px' : 'calc((100vw - var(--body-width-max) - var(--space-30)) / 2)'}
  style="--color-theme: {themeColor}; --color-theme-focus: {themeFocusColor}; z-index: {zIndex}; --desktop-breakpoint:{DESKTOP_BREAKPOINT}px; --nav-width:{clientWidth}px;"
>
  <Header {data} {imageSubnavData} {ctaSubnavData} {desktopMode} {mobileMode} />
</div>

<style>.PublicWebNav {
  position: relative;
  --space-10: 10px;
  --space-30: 30px;
  --color-theme: #006aff;
  --color-theme-focus: #05c;
}
.PublicWebNav,
.PublicWebNav :global(.lightMode) {
  --color-text: #1a1a1a;
  --color-background: #fff;
  --color-background-rgb: 255, 255, 255;
  --color-border: #d9d9d9;
  --color-highlight: rgba(26, 26, 26, 0.1);
  --color-gray: #737373;
}
.PublicWebNav.darkMode,
.PublicWebNav :global(.darkMode) {
  --color-text: #fff;
  --color-background: #030303;
  --color-background-rgb: 0, 0, 0;
  --color-border: #333;
  --color-highlight: rgba(255, 255, 255, 0.1);
  --color-gray: #b3b3b3;
}
.PublicWebNav :global(nav.DesktopNav) {
  padding-left: 22px;
  padding-right: 22px;
}
@media (min-width: 1292px) {
  .PublicWebNav :global(nav.DesktopNav) {
    --nav-outer-gutter-base: calc(var(--space-10) / 2);
    --nav-outer-gutter: max(var(--nav-outer-gutter-base), var(--dynamic-gutter));
    padding-left: calc(22px + var(--nav-outer-gutter)) !important;
    padding-right: calc(22px + var(--nav-outer-gutter)) !important;
  }
}
.PublicWebNav :global(.CtaSubnavDesktopNav .header) {
  padding-left: 32px;
  padding-right: 32px;
}
@media (min-width: 1292px) {
  .PublicWebNav :global(.CtaSubnavDesktopNav .header) {
    --nav-outer-gutter-base: calc(var(--space-10) / 2);
    --nav-outer-gutter: max(var(--nav-outer-gutter-base), var(--dynamic-gutter));
    padding-left: calc(32px + var(--nav-outer-gutter)) !important;
    padding-right: calc(22px + var(--nav-outer-gutter)) !important;
  }
}</style>
